import { useCallback } from "react";
import { convertDistance } from "geolib";
import { SpeedUnits, LengthUnits, VolumeUnits, ShortLengthUnit, ShortVolumeUnits, CoordinatesFormats, } from "../../types";
import { isNil } from "../../utilities/isNil";
import { useUnitSystemContext } from "../../hooks";
var MinimumDepthValuesPerUnit;
(function (MinimumDepthValuesPerUnit) {
    MinimumDepthValuesPerUnit[MinimumDepthValuesPerUnit["FEET"] = 0.13] = "FEET";
    MinimumDepthValuesPerUnit[MinimumDepthValuesPerUnit["METERS"] = 0.04] = "METERS";
    MinimumDepthValuesPerUnit[MinimumDepthValuesPerUnit["FATHOM"] = 0.02] = "FATHOM";
})(MinimumDepthValuesPerUnit || (MinimumDepthValuesPerUnit = {}));
export const convertToGallons = (volume) => {
    return volume * 0.26417;
};
export const convertToKnots = (speed, unit) => {
    let value = speed;
    switch (unit) {
        case SpeedUnits.KPH:
            value *= 0.53996;
            break;
        case SpeedUnits.MPH:
        default:
            value *= 0.86898;
    }
    return value;
};
export const convertToMeters = (length, unit) => {
    let value = length;
    switch (unit) {
        case LengthUnits.KILOMETERS:
            value *= 0.001;
            break;
        case LengthUnits.MILES:
            value *= 1609.344;
            break;
        case LengthUnits.NAUTICAL_MILE:
            value *= 1852;
            break;
        case LengthUnits.FATHOM:
            value *= 1.829;
            break;
        case LengthUnits.FEET:
        default:
            value *= 0.3048;
    }
    return value;
};
/**
 *
 * @param originalValue value to be handled.
 * @param originalUnit type of base unit to handle –defines start point for math calculation–.
 * @param fixDecimal set the number of decimal places.
 * @returns
 */
export const useUnitSystem = () => {
    const { fuelUnit, depthUnit, speedUnit, coordinatesFormat } = useUnitSystemContext();
    /**
     * Length Unit Handler
     */
    const lengthUnitHandler = useCallback(({ originalUnit, originalValue, fixDecimal = true, }) => {
        let initialValue = originalValue;
        let unit = originalUnit;
        if (unit !== LengthUnits.METERS) {
            initialValue = convertToMeters(initialValue, unit);
        }
        switch (depthUnit) {
            case LengthUnits.FEET:
            case LengthUnits.FATHOM:
                unit = ShortLengthUnit.FtString;
                initialValue = convertDistance(initialValue, ShortLengthUnit.FtString);
                break;
            case LengthUnits.METERS:
            default:
                unit = ShortLengthUnit.MtString;
        }
        const value = fixDecimal ? initialValue === null || initialValue === void 0 ? void 0 : initialValue.toFixed(1) : initialValue;
        return { value, unit };
    }, [depthUnit]);
    const depthUnitHandler = useCallback(({ originalUnit, originalValue, fixDecimal = true, validateMinimumThreshold, }) => {
        let initialValue = originalValue;
        let unit = originalUnit;
        if (unit !== LengthUnits.METERS) {
            initialValue = convertToMeters(initialValue, unit);
        }
        switch (depthUnit) {
            case LengthUnits.FEET:
                unit = ShortLengthUnit.FtString;
                initialValue = convertDistance(initialValue, ShortLengthUnit.FtString);
                if ((validateMinimumThreshold &&
                    initialValue <= MinimumDepthValuesPerUnit.FEET) ||
                    Number.isNaN(initialValue)) {
                    initialValue = "-";
                    fixDecimal = false;
                }
                break;
            case LengthUnits.FATHOM:
                unit = ShortLengthUnit.FathString;
                initialValue *= 0.546807;
                if ((validateMinimumThreshold &&
                    initialValue <= MinimumDepthValuesPerUnit.FATHOM) ||
                    Number.isNaN(initialValue)) {
                    initialValue = "-";
                    fixDecimal = false;
                }
                break;
            case LengthUnits.METERS:
                unit = ShortLengthUnit.MtString;
                if ((validateMinimumThreshold &&
                    initialValue <= MinimumDepthValuesPerUnit.METERS) ||
                    Number.isNaN(initialValue)) {
                    initialValue = "-";
                    fixDecimal = false;
                }
                break;
            default:
                unit = ShortLengthUnit.MtString;
        }
        const value = fixDecimal && typeof initialValue === "number"
            ? initialValue === null || initialValue === void 0 ? void 0 : initialValue.toFixed(1)
            : initialValue;
        return { value, unit };
    }, [depthUnit]);
    const distanceUnitHandler = useCallback(({ originalUnit, originalValue, fixDecimal = true, }) => {
        let initialValue = originalValue;
        let unit = originalUnit;
        if (unit !== LengthUnits.METERS) {
            initialValue = convertToMeters(initialValue, unit);
        }
        switch (speedUnit) {
            case SpeedUnits.KNOT:
                unit = ShortLengthUnit.NmString;
                initialValue = convertDistance(initialValue, "sm");
                break;
            case SpeedUnits.MPH:
                unit = ShortLengthUnit.MiString;
                initialValue = convertDistance(initialValue, ShortLengthUnit.MiString);
                break;
            case SpeedUnits.KPH:
            default:
                initialValue = convertDistance(initialValue, ShortLengthUnit.KmString);
                unit = ShortLengthUnit.KmString;
        }
        const value = fixDecimal ? initialValue === null || initialValue === void 0 ? void 0 : initialValue.toFixed(1) : initialValue;
        return { value, unit };
    }, [speedUnit]);
    /**
     * Consumption Unit Handler
     */
    const consumptionUnitHandler = useCallback(({ originalUnit, originalValue, fixDecimal = true, }) => {
        let initialValue = originalValue;
        let unit = originalUnit;
        if (unit !== VolumeUnits.GALLONS) {
            initialValue *= 0.26417;
        }
        switch (fuelUnit) {
            case VolumeUnits.LITERS:
                unit = ShortVolumeUnits.LString;
                initialValue *= 3.78541;
                break;
            default:
                unit = ShortVolumeUnits.GalString;
        }
        const value = fixDecimal ? initialValue === null || initialValue === void 0 ? void 0 : initialValue.toFixed(1) : initialValue;
        return { value, unit };
    }, [fuelUnit]);
    /**
     * Speed Unit Handler
     */
    const speedUnitHandler = useCallback(({ originalUnit, originalValue, fixDecimal = true, }) => {
        let initialValue = originalValue;
        let unit = originalUnit;
        if (unit !== SpeedUnits.KNOT) {
            initialValue = convertToKnots(initialValue, unit);
        }
        switch (speedUnit) {
            case SpeedUnits.MPH:
                unit = SpeedUnits.MPH;
                initialValue *= 1.150779;
                break;
            case SpeedUnits.KPH:
                unit = SpeedUnits.KPH;
                initialValue *= 1.852;
                break;
            case SpeedUnits.KNOT:
            default:
                unit = SpeedUnits.KNOT;
        }
        const value = fixDecimal ? initialValue === null || initialValue === void 0 ? void 0 : initialValue.toFixed(1) : initialValue;
        return { value, unit };
    }, [speedUnit]);
    /**
      * More Than Six Decimals
      */
    const moreThanSixDecimals = useCallback((value) => { var _a, _b, _c; return ((_c = (_b = (_a = String(value)) === null || _a === void 0 ? void 0 : _a.split('.')) === null || _b === void 0 ? void 0 : _b[1]) === null || _c === void 0 ? void 0 : _c.length) > 6; }, []);
    /* * Coordinates Section * */
    const convertToLatLngFromDMS = (latitude, longitude) => {
        var _a, _b, _c, _d;
        const lat = Math.trunc(Number(latitude === null || latitude === void 0 ? void 0 : latitude.degrees)) +
            Number((_a = latitude === null || latitude === void 0 ? void 0 : latitude.minutes) !== null && _a !== void 0 ? _a : 0) / 60 +
            Number((_b = latitude === null || latitude === void 0 ? void 0 : latitude.seconds) !== null && _b !== void 0 ? _b : 0) / 3600;
        const lng = Math.trunc(Number(longitude === null || longitude === void 0 ? void 0 : longitude.degrees)) +
            Number((_c = longitude === null || longitude === void 0 ? void 0 : longitude.minutes) !== null && _c !== void 0 ? _c : 0) / 60 +
            Number((_d = longitude === null || longitude === void 0 ? void 0 : longitude.seconds) !== null && _d !== void 0 ? _d : 0) / 3600;
        return { lat, lng };
    };
    const convertToDMSFromLatLng = (latitude, longitude) => {
        const degreeLat = Math.trunc(latitude);
        const minutesLat = Number((latitude - Math.trunc(latitude)) * 60);
        const secondsLat = Number((minutesLat - Math.trunc(minutesLat)) * 60);
        const degreeLng = Math.abs(Math.trunc(longitude));
        const minutesLng = Number((longitude - Math.trunc(longitude)) * 60);
        const secondsLng = Number((minutesLng - Math.trunc(minutesLng)) * 60);
        return {
            lat: { degrees: degreeLat, minutes: minutesLat, seconds: secondsLat },
            lng: { degrees: degreeLng, minutes: minutesLng, seconds: secondsLng },
        };
    };
    /**
     * Coordinate Format Handler
     */
    const coordinatesFormatHandler = useCallback(({ latitude, longitude }) => {
        if (isNil(latitude) || isNil(longitude))
            return "";
        const hemisphereNS = latitude > 0 ? "N" : "S";
        const hemisphereWE = longitude < 0 ? "W" : "E";
        const { lat, lng } = convertToDMSFromLatLng(latitude, longitude);
        switch (coordinatesFormat) {
            case CoordinatesFormats.DDM:
                return `${lat.degrees}°${Math.abs(lat.minutes).toFixed(4)}'${hemisphereNS} ${lng.degrees}°${Math.abs(lng.minutes).toFixed(4)}'${hemisphereWE}`;
            case CoordinatesFormats.DMS:
                return `${lat.degrees}°${Math.abs(Math.trunc(lat.minutes))}'${Math.abs(lat.seconds).toFixed(2)}"${hemisphereNS} ${lng.degrees}°${Math.abs(Math.trunc(lng.minutes))}'${Math.abs(lng.seconds).toFixed(2)}"${hemisphereWE}`;
            case CoordinatesFormats.DD:
            default:
                return `${moreThanSixDecimals(latitude) ? latitude.toFixed(6) : latitude}, ${moreThanSixDecimals(longitude) ? longitude.toFixed(6) : longitude}`;
        }
    }, [coordinatesFormat]);
    return {
        speedUnitHandler,
        depthUnitHandler,
        lengthUnitHandler,
        distanceUnitHandler,
        consumptionUnitHandler,
        convertToLatLngFromDMS,
        convertToDMSFromLatLng,
        coordinatesFormatHandler,
    };
};
//# sourceMappingURL=useUnitSystem.js.map